import React from 'react'
import { Card, CardContent, CardHeader } from '@/shared/ui'
import { tfLabelPT } from '../utils/contextText'
import { useSignalsEnabled } from '@/modules/signals/hooks/useSignalsEnabled'
import { useOpportunitySignals } from '@/modules/signals/hooks/useOpportunitySignals'
import { useLastPrice } from '@/modules/signals/hooks/useLastPrice'
import {
  fetchSignalsMultiTfState,
  fetchSignalsMultiTfDirectionConflicts,
  type OpportunityMultiTfState,
  type OpportunityMultiTfDirectionConflict,
} from '@/modules/signals'
import { shouldShowMultiTfCriticalWarning } from '@/modules/signals/utils/multiTfWarning'

type Props = {
  data: any
}

export function SignalsMultiTfWarningCard({ data }: Props) {
  const idNum = Number((data as any)?.id)
  const symbol = String((data as any)?.symbol || '').toUpperCase()

  if (!Number.isFinite(idNum) || !symbol) return null

  const signalsEnabled = useSignalsEnabled()
  const isSignalsOn = signalsEnabled === true

  const { signals } = useOpportunitySignals(idNum, isSignalsOn)
  const lastPrice = useLastPrice(symbol)

  const [multiState, setMultiState] =
    React.useState<OpportunityMultiTfState | null>(null)
  const [multiDirection, setMultiDirection] =
    React.useState<OpportunityMultiTfDirectionConflict | null>(null)

  React.useEffect(() => {
    if (!isSignalsOn || !Number.isFinite(idNum)) {
      setMultiState(null)
      setMultiDirection(null)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const [stateRes, conflictRes] = await Promise.all([
          fetchSignalsMultiTfState([idNum]),
          fetchSignalsMultiTfDirectionConflicts([idNum]),
        ])
        if (cancelled) return
        setMultiState(stateRes[String(idNum)] ?? null)
        setMultiDirection(conflictRes[String(idNum)] ?? null)
      } catch {
        if (!cancelled) {
          setMultiState(null)
          setMultiDirection(null)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [idNum, isSignalsOn])

  const price = typeof lastPrice === 'number' ? lastPrice : null
  const criticalWarning = shouldShowMultiTfCriticalWarning({
    multiTfState: multiState?.state,
    signals: signals ?? null,
    lastPrice: price,
  })

  const directionConflictWarning = !!multiDirection?.hasConflict

  if (!criticalWarning && !directionConflictWarning) return null

  const tfAtualLabel =
    tfLabelPT((data as any)?.target_timeframe) ||
    tfLabelPT((data as any)?.timeframe) ||
    '-'

  let title: string
  let content: string

  if (criticalWarning && multiState?.parentTf) {
    const tfMaiorLabel = tfLabelPT(multiState.parentTf) || multiState.parentTf
    title = 'Oportunidade em zona crítica.'
    content = `Essa oportunidade em ${tfAtualLabel} aumentou as chances de atingir a zona de stop e iniciar uma correção no ${tfMaiorLabel}.`
  } else if (directionConflictWarning && multiDirection?.higherTf) {
    const tfMaiorLabel =
      tfLabelPT(multiDirection.higherTf) || multiDirection.higherTf
    title = `Atenção: Timeframes ${tfAtualLabel} e ${tfMaiorLabel} em direções opostas`
    content = `${tfAtualLabel} menor gerou oportunidade enquanto o ${tfMaiorLabel} maior aponta direção oposta. Ambos estão corretos conforme a metodologia. Aviso criado apenas para dar mais contexto à sua decisão.`
  } else {
    return null
  }

  return (
    <Card className="border-amber-400/40 bg-amber-500/10">
      <CardHeader className="pb-1 text-sm font-semibold text-amber-300">
        {title}
      </CardHeader>
      <CardContent className="pt-0 text-xs text-amber-100">
        {content}
      </CardContent>
    </Card>
  )
}

